import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, fbrApi } from '@/services/api';
import { formatCnicOrNtn } from '@/utils/cnicNtnUtils';
import {
  FileCheck,
  ShieldAlert,
  Save,
  Loader2,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
  Radio,
  Server,
  Building,
  Info,
} from 'lucide-react';

export default function FBRIntegrationTab() {
  const qc = useQueryClient();
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const [form, setForm] = useState({
    enable_fbr_integration: false,
    fbr_environment: 'Sandbox' as 'Sandbox' | 'Production',
    fbr_pos_id: '',
    fbr_ntn: '',
    fbr_strn: '',
    fbr_bearer_token: '',
    fbr_seller_province: 'Punjab',
    fbr_business_category: 'Tier-1 Retailer',
    fbr_default_pct_code: '00000000',
    fbr_fail_safe_mode: 'Queue & Retry' as 'Queue & Retry' | 'Strict Block',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        enable_fbr_integration: profile.enable_fbr_integration === 1 || profile.enable_fbr_integration === true,
        fbr_environment: profile.fbr_environment || 'Sandbox',
        fbr_pos_id: profile.fbr_pos_id || '',
        fbr_ntn: profile.fbr_ntn || '',
        fbr_strn: profile.fbr_strn || '',
        fbr_bearer_token: profile.fbr_bearer_token || '',
        fbr_seller_province: profile.fbr_seller_province || 'Punjab',
        fbr_business_category: profile.fbr_business_category || 'Tier-1 Retailer',
        fbr_default_pct_code: profile.fbr_default_pct_code || '00000000',
        fbr_fail_safe_mode: profile.fbr_fail_safe_mode || 'Queue & Retry',
      });
    }
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (data: any) => authApi.updateCompanyProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setSaveSuccess(true);
      setSaveError('');
      setTimeout(() => setSaveSuccess(false), 4000);
    },
    onError: (err: any) => {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save FBR settings');
      setSaveSuccess(false);
    },
  });

  const testConnectionMutation = useMutation({
    mutationFn: (data: any) => fbrApi.testConnection(data),
    onSuccess: (res) => {
      const msg = res.data.message;
      setTestResult({
        success: msg?.success ?? true,
        message: msg?.message || 'FBR Connection Successful',
        details: msg?.response,
      });
    },
    onError: (err: any) => {
      setTestResult({
        success: false,
        message: err.response?.data?.message || err.message || 'FBR Connection Failed',
      });
    },
  });

  const retryPendingMutation = useMutation({
    mutationFn: () => fbrApi.retryPendingInvoices(),
    onSuccess: (res) => {
      const msg = res.data.message;
      setSyncResult({
        success: msg?.success ?? true,
        message: msg?.message || 'Sync operation completed',
      });
      setTimeout(() => setSyncResult(null), 5000);
    },
    onError: (err: any) => {
      setSyncResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to sync pending invoices',
      });
    },
  });

  const PAK_PROVINCES = [
    'Punjab',
    'Sindh',
    'Khyber Pakhtunkhwa',
    'Balochistan',
    'Islamabad Capital Territory',
    'Gilgit-Baltistan',
    'Azad Jammu and Kashmir',
  ];

  const FBR_BUSINESS_CATEGORIES = [
    'Tier-1 Retailer',
    'Wholesaler / Distributor',
    'Services Provider',
    'Manufacturer',
  ];

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');

    if (form.enable_fbr_integration) {
      if (!form.fbr_seller_province) {
        setSaveError('FBR Registered Province is required when FBR integration is enabled.');
        return;
      }
      if (!form.fbr_pos_id || !form.fbr_ntn) {
        setSaveError('FBR POS ID and Business NTN are required when FBR integration is enabled.');
        return;
      }
    }

    saveMutation.mutate(form);
  };

  const isFbrIncomplete =
    form.enable_fbr_integration &&
    (!form.fbr_seller_province || !form.fbr_pos_id || !form.fbr_ntn || !form.fbr_strn || !form.fbr_bearer_token);

  const handleTestConnection = () => {
    setTestResult(null);
    testConnectionMutation.mutate({
      fbr_environment: form.fbr_environment,
      fbr_pos_id: form.fbr_pos_id,
      fbr_ntn: form.fbr_ntn,
      fbr_strn: form.fbr_strn,
      fbr_bearer_token: form.fbr_bearer_token,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if ((profile?.country || 'GB') !== 'PK') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        FBR Integration is only available for Pakistan. Change your company country to Pakistan to configure FBR.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Banner / Header */}
      <div className="bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900 text-white rounded-2xl p-6 shadow-xl border border-emerald-800/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 translate-x-6 -translate-y-6 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-xl border border-emerald-500/30">
                <FileCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-tight text-white">FBR Digital Invoice Integration</h2>
                <p className="text-xs text-slate-300">
                  Real-time sales & return invoice fiscalization with Federal Board of Revenue Pakistan
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full border flex items-center gap-1.5 ${
                form.enable_fbr_integration
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                  : 'bg-slate-700/50 text-slate-400 border-slate-600'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  form.enable_fbr_integration ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'
                }`}
              ></span>
              {form.enable_fbr_integration ? 'FBR Integration Enabled' : 'Integration Disabled'}
            </span>

            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-slate-800 text-slate-300 border border-slate-700">
              {form.fbr_environment} Mode
            </span>
          </div>
        </div>
      </div>

      {isFbrIncomplete && (
        <div className="p-4 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl flex items-start gap-3 shadow-sm">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold block text-amber-900">FBR Setup Incomplete</span>
            Please select your business registered Province and enter valid FBR credentials (POS ID, NTN, STRN, Bearer Token) to ensure compliant fiscal invoice generation.
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-medium">FBR Integration settings updated successfully!</span>
        </div>
      )}

      {saveError && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0" />
          <span className="text-sm font-medium">{saveError}</span>
        </div>
      )}

      {testResult && (
        <div
          className={`p-4 rounded-xl border flex flex-col gap-2 ${
            testResult.success
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold text-sm">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            ) : (
              <ShieldAlert className="w-5 h-5 text-amber-600" />
            )}
            <span>{testResult.message}</span>
          </div>
          {testResult.details && (
            <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto max-h-40">
              {JSON.stringify(testResult.details, null, 2)}
            </pre>
          )}
        </div>
      )}

      {syncResult && (
        <div
          className={`p-4 rounded-xl border flex items-center gap-3 ${
            syncResult.success ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-rose-50 border-rose-200 text-rose-900'
          }`}
        >
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <span className="text-sm font-medium">{syncResult.message}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Enable Toggle & Environment */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Enable FBR Integration</h3>
              <p className="text-xs text-slate-500">
                Automatically generate and transmit sales & return invoices to FBR Pakistan
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.enable_fbr_integration}
                onChange={(e) => setForm({ ...form, enable_fbr_integration: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          {/* Environment Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label
              onClick={() => setForm({ ...form, fbr_environment: 'Sandbox' })}
              className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                form.fbr_environment === 'Sandbox'
                  ? 'border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-500/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Radio
                className={`w-5 h-5 mt-0.5 ${
                  form.fbr_environment === 'Sandbox' ? 'text-emerald-600' : 'text-slate-400'
                }`}
              />
              <div>
                <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                  <span>Sandbox (Testing Mode)</span>
                  <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-bold">
                    TEST
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Safely test POS invoice submission & QR code receipt printing without live tax submission
                </p>
              </div>
            </label>

            <label
              onClick={() => setForm({ ...form, fbr_environment: 'Production' })}
              className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                form.fbr_environment === 'Production'
                  ? 'border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-500/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Radio
                className={`w-5 h-5 mt-0.5 ${
                  form.fbr_environment === 'Production' ? 'text-emerald-600' : 'text-slate-400'
                }`}
              />
              <div>
                <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                  <span>Production (Live System)</span>
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold">
                    LIVE
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Connect directly to FBR Production servers using your issued POS ID and Authorization Token
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Credentials & Business Details */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-6">
          <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
            <Building className="w-5 h-5 text-emerald-600" />
            <h3 className="text-base font-semibold text-slate-900">Business Credentials & Identifiers</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                FBR Registered Province <span className="text-rose-500">*</span>
              </label>
              <select
                value={form.fbr_seller_province}
                onChange={(e) => setForm({ ...form, fbr_seller_province: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              >
                {PAK_PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">Province of registration with FBR / Revenue Authority</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Business Category
              </label>
              <select
                value={form.fbr_business_category}
                onChange={(e) => setForm({ ...form, fbr_business_category: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              >
                {FBR_BUSINESS_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                FBR POS ID <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 123456"
                value={form.fbr_pos_id}
                onChange={(e) => setForm({ ...form, fbr_pos_id: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
              />
              <p className="text-[11px] text-slate-500 mt-1">6-digit unique POS ID provided by FBR</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Business CNIC/NTN <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 1234567-8 or 35201-1234567-1"
                maxLength={15}
                value={form.fbr_ntn}
                onChange={(e) => setForm({ ...form, fbr_ntn: formatCnicOrNtn(e.target.value) })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
              />
              <p className="text-[11px] text-slate-500 mt-1">13-digit CNIC (XXXXX-XXXXXXX-X) or 8-digit NTN (XXXXXXX-X)</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Sales Tax Registration Number (STRN)
              </label>
              <input
                type="text"
                placeholder="e.g. 1234567890123"
                value={form.fbr_strn}
                onChange={(e) => setForm({ ...form, fbr_strn: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Default PCT / HS Code
              </label>
              <input
                type="text"
                placeholder="e.g. 00000000"
                value={form.fbr_default_pct_code}
                onChange={(e) => setForm({ ...form, fbr_default_pct_code: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Fallback PCT code if item-specific PCT code is not configured
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
              FBR Bearer Authorization Token
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                placeholder="Enter Bearer Token issued by FBR"
                value={form.fbr_bearer_token}
                onChange={(e) => setForm({ ...form, fbr_bearer_token: e.target.value })}
                className="w-full pl-4 pr-12 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              API Authorization Bearer token provided in your FBR POS registration portal
            </p>
          </div>
        </div>

        {/* Operational Fail-Safe Settings */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
          <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
            <Server className="w-5 h-5 text-emerald-600" />
            <div>
              <h3 className="text-base font-semibold text-slate-900">System Downtime & Fail-Safe Strategy</h3>
              <p className="text-xs text-slate-500">
                Configure how the POS handles sales when FBR servers are offline or unresponsive
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label
              onClick={() => setForm({ ...form, fbr_fail_safe_mode: 'Queue & Retry' })}
              className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                form.fbr_fail_safe_mode === 'Queue & Retry'
                  ? 'border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-500/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Radio
                className={`w-5 h-5 mt-0.5 ${
                  form.fbr_fail_safe_mode === 'Queue & Retry' ? 'text-emerald-600' : 'text-slate-400'
                }`}
              />
              <div>
                <div className="font-semibold text-sm text-slate-900">Queue & Retry (Recommended)</div>
                <p className="text-xs text-slate-500 mt-1">
                  Invoice is issued immediately to customer. If FBR server is unreachable, invoice is queued and auto-retried in background without delaying checkout.
                </p>
              </div>
            </label>

            <label
              onClick={() => setForm({ ...form, fbr_fail_safe_mode: 'Strict Block' })}
              className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                form.fbr_fail_safe_mode === 'Strict Block'
                  ? 'border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-500/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Radio
                className={`w-5 h-5 mt-0.5 ${
                  form.fbr_fail_safe_mode === 'Strict Block' ? 'text-emerald-600' : 'text-slate-400'
                }`}
              />
              <div>
                <div className="font-semibold text-sm text-slate-900">Strict Compliance Block</div>
                <p className="text-xs text-slate-500 mt-1">
                  Prevents invoice generation if FBR fails to return real-time FBR Invoice ID. Use only if strict real-time FBR receipt rules are required.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testConnectionMutation.isPending}
              className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {testConnectionMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
              ) : (
                <Radio className="w-4 h-4 text-emerald-600" />
              )}
              Test FBR Connection
            </button>

            <button
              type="button"
              onClick={() => retryPendingMutation.mutate()}
              disabled={retryPendingMutation.isPending || !form.enable_fbr_integration}
              className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {retryPendingMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
              ) : (
                <RefreshCw className="w-4 h-4 text-emerald-600" />
              )}
              Sync Queued Invoices
            </button>
          </div>

          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm shadow-lg shadow-emerald-600/20 flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save FBR Settings
          </button>
        </div>
      </form>
    </div>
  );
}
